import DOMPurify from "dompurify";

export const capitalizeFirstLetter = (word: string): string => {
  if (!word) {
    return "";
  }

  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

export const toSnakeCase = (str: string) =>
  str.toLowerCase().replace(/\s+/g, "_");

// Utility function to transform a camelCase string to Title Case
export function transformCamelCaseToTitleCase(str: string): string {
  // First, handle the case for completely uppercase strings
  if (str === str.toUpperCase()) {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  return str
    .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space before uppercase letters
    .replace(/^./, (s) => s.toUpperCase()) // Capitalize the first letter
    .trim(); // Remove any leading/trailing whitespace
}

export function transformSnakeCaseToTitleCase(str: string): string {
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function toCamelCase(str: string): string {
  return (
    str
      // Replace any non-alphanumeric character (including spaces, underscores, hyphens) with a space
      .replace(/[^a-zA-Z0-9]+/g, " ")
      // Trim leading/trailing spaces
      .trim()
      // Convert to lowercase first
      .toLowerCase()
      // Capitalize letters after spaces and remove spaces
      .replace(/ (.)/g, (_, group1) => group1.toUpperCase())
  );
}

export const isHTMLString = (str: string): boolean => {
  const doc = new DOMParser().parseFromString(str, "text/html");

  return Array.from(doc.body.childNodes).some((node) => node.nodeType === 1);
};

export function HtmlRenderer({
  htmlString,
  className,
}: {
  htmlString: string;
  className?: string;
}) {
  const sanitizedHtml = DOMPurify.sanitize(htmlString, {
    ALLOWED_TAGS: [
      "b",
      "i",
      "em",
      "span",
      "strong",
      "a",
      "br",
      "ul",
      "li",
      "div",
    ], // specify allowed tags (removed div)
    ALLOWED_ATTR: ["href", "target", "rel"], // specify allowed attributes
  });

  return isHTMLString(htmlString) ? (
    <span
      className={className}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  ) : (
    htmlString
  );
}
